import classNames from 'classnames'
import { motion } from 'framer-motion'

import { useManagementForceCancelUnitActivation } from '@apps-common/apis/management/unit-activation'
import {
  useActionUnitActivation,
  useArchiveUnitActivation,
  useCancelUnitActivation,
  useDeleteUnitActivation
} from '@apps-common/apis/portal/unit-activations'
import { UnitActivationOptionsModal } from '@apps-common/apps/portal/pages/units/pages/activations/modals'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonCopy, ButtonRoundSm } from '@apps-common/components/elements/button'
import { VertSpacerSm, VertSpacerXS } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { GRID_CARD_VARIANTS } from '@apps-common/config/motion-variants'
import { UnitActivationAction, UnitActivationStatus } from '@apps-common/config/unit-activation'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import { ROUTE_EDIT_UNIT_ACTIVATION, ROUTE_UNIT_ACTIVATION_DETAILS } from '@apps-radionet-portal/config/private-routes'
import { invalidateActivationQueries } from '../../lib/invalidateActivationQueries'
import { UnitActivationStatusPill } from '../UnitActivationStatusPill'

interface IProps {
  bgClasses: string
  textClasses: string
  iconClasses: string
  bgSecondaryClasses: string
  textSecondaryClasses: string
  bgTertiaryClasses: string
  activation: IActivationServiceRequestModel
}

export const UnitActivationCard: React.FC<IProps> = (props) => {
  const { bgClasses, textClasses, bgSecondaryClasses, textSecondaryClasses, bgTertiaryClasses, activation } = props
  const { unitActivationRequestId, status, accountNo, imei, serialNo, mCentre, productImageUrl, archived } = activation
  const { navigateForward } = useNavigation()

  const { mutate: actionActivation, isPending: actionActivationIsPending } = useActionUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )
  const { mutate: archiveActivation, isPending: archiveActivationIsPending } = useArchiveUnitActivation(true, (data) =>
    invalidateActivationQueries()
  )
  const { mutate: cancelActivation, isPending: cancelActivationIsPending } = useCancelUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )
  const { mutate: deleteActivation, isPending: deleteActivationIsPending } = useDeleteUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )
  const { mutate: forceCancelActivation, isPending: forceCancelIsPending } = useManagementForceCancelUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )

  const onOptionSelected = (action: string) => {
    if (action === UnitActivationAction.forceCancel) {
      forceCancelActivation({ id: unitActivationRequestId })
    } else if (action === UnitActivationAction.activateNow) {
      actionActivation({ model: activation as any, action })
    } else if (action === UnitActivationAction.powerUp) {
      actionActivation({ model: activation as any, action })
    } else if (action === UnitActivationAction.edit) {
      navigateForward({
        routeUrl: ROUTE_EDIT_UNIT_ACTIVATION.buildPath(activation.unitActivationRequestId),
        route: ROUTE_EDIT_UNIT_ACTIVATION
      })
    } else if (action === UnitActivationAction.delete) {
      deleteActivation({ id: unitActivationRequestId || null })
    } else if (action === UnitActivationAction.archive) {
      archiveActivation({ id: unitActivationRequestId || null })
    } else if (action === UnitActivationAction.cancel) {
      cancelActivation({ id: unitActivationRequestId || null })
    }
  }

  const accountNoDisplay = () => {
    if (status === UnitActivationStatus.cancelled) {
      return <span className='text-[0.85em]'>TBC</span>
    }

    return accountNo || <span className='text-[0.85em]'>None</span>
  }

  const mcName = mCentre || 'Monitoring Centre'

  const loading = actionActivationIsPending || archiveActivationIsPending || cancelActivationIsPending || deleteActivationIsPending

  return (
    <motion.div
      variants={GRID_CARD_VARIANTS}
      className={classNames(
        'relative rounded-[36px] px-5 py-4 min-w-[160px] max-w-[250px] overflow-hidden border border-border',
        bgClasses
      )}
    >
      <div className='flex items-center justify-start'>
        <h4 className={classNames('jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden', textClasses)}>{mcName}</h4>
      </div>
      <div className='flex items-center justify-between gap-[10px]'>
        <div
          className={classNames('px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0', bgSecondaryClasses)}
        >
          <commonIcons.processor className={classNames('', textSecondaryClasses)} />
          <h5 className={classNames('jost-light text-[0.85rem] uppercase', textClasses)}>{accountNoDisplay()}</h5>
        </div>
        <div
          className={classNames('w-[45px] h-[45px] rounded-[23px] flex items-center justify-center overflow-hidden', bgSecondaryClasses)}
        >
          <ImageComponent
            src={productImageUrl}
            alt={`Product ${accountNo}`}
            imageClasses='max-w-[30px]'
            errorIconClasses={classNames('text-[25px]', textClasses)}
          />
        </div>
      </div>
      <div className='text-[0.8em] flex items-center justify-start gap-[5px] mb-2'>
        <UnitActivationStatusPill status={status || 'unknown'} />
        {/* {!archived && <commonIcons.archive className='text-[11px]' />} */}
      </div>
      <VertSpacerXS />
      <p className={classNames('jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden flex', textClasses)}>
        <span className={classNames('jost-medium px-[3px] rounded-md mr-[2px] text-[0.8em]', bgTertiaryClasses, textSecondaryClasses)}>
          SN
        </span>
        {serialNo} <ButtonCopy value={serialNo} iconClassName='text-[13px]' />
      </p>
      <p className={classNames('jost text-[0.75rem] text-ellipsis whitespace-nowrap overflow-hidden flex', textClasses)}>
        <span className={classNames('jost-medium text-[0.9em] px-[3px] rounded-md mr-[2px]', bgTertiaryClasses, textSecondaryClasses)}>
          IMEI
        </span>
        {imei} <ButtonCopy value={imei} iconClassName='text-[13px]' />
      </p>
      <VertSpacerSm />
      <div className='flex items-center justify-between gap-x-2 mt-2'>
        {!archived && (
          <ModalButton
            component={UnitActivationOptionsModal}
            disabled={false}
            classes='grow'
            componentProps={{
              activation
            }}
            confirmCallback={(data) => onOptionSelected(data?.action)}
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <ButtonRoundSm type='div' disabled={loading} className='action-success !w-[35px] !h-[35px]'>
              <commonIcons.settings />
            </ButtonRoundSm>
          </ModalButton>
        )}
        <button
          type='button'
          className={classNames(
            'action-brand grow flex items-center justify-between gap-[5px] jost-light text-[0.8rem] !pl-[15px] !p-[5px] h-[35px] rounded-full'
            // bgSecondaryClasses,
            // textSecondaryClasses
          )}
          disabled={loading}
          onClick={(e) => {
            e?.stopPropagation()
            navigateForward({
              routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(unitActivationRequestId),
              route: ROUTE_UNIT_ACTIVATION_DETAILS
            })
          }}
        >
          Details
          <div className='bg-brand text-on-brand w-[26px] h-[26px] rounded-[13px] flex items-center justify-center'>
            <commonIcons.arrowAngled className='text-[0.9rem]' />
          </div>
        </button>
      </div>
    </motion.div>
  )
}
